import {FC} from 'react'
import {get} from 'lodash'
import {connect} from 'react-redux'
import {Edit} from 'jbc-front/components/icons'
import styles from 'employees/show/Parts.scss'

import type {Employee} from 'types/api/employees/employee'
import type {State} from 'types/state'
import type {HealthInsurance} from 'types/api/employees/healthInsurance'
import type {WelfarePensionInsurance} from 'types/api/employees/welfarePensionInsurance'
import type {HealthInsuranceType} from 'types/api/offices/office'
import type {YearStyleType} from 'libs/formatter'

// @ts-ignore
import {Panel, Item, Headding} from 'components/PrimaryPanel'
// @ts-ignore
import {recordDisplay, addDay} from 'utils'
// @ts-ignore
import {HashLink} from 'react-router-hash-link'
// @ts-ignore
import {Dl, Dd, Dt, Half, Grid, SubPanel} from 'employees/show/Parts'

interface InsuranceProps {
  retired_at?: string
  healthInsuranceType: HealthInsuranceType
  editLink: string
  yearStyle: YearStyleType
}

interface HealthInsuranceProps extends InsuranceProps {
  healthInsurance: HealthInsurance
}

const HealthInsurance: FC<HealthInsuranceProps> = ({
  healthInsurance,
  retired_at,
  healthInsuranceType,
  editLink,
  yearStyle,
}) => {
  const healthInsuranceEndOn = healthInsurance.end_on || addDay(retired_at, 1)

  return (
    <SubPanel>
      <HashLink to={`${editLink}#HealthInsuranceInfomation`}>
        <SubPanel.Headding icon={<Edit size={16} />}>健康保険</SubPanel.Headding>
      </HashLink>
      <Grid className={styles.panelHalf}>
        <Dl>
          <Dt>加入状況</Dt>
          <Dd>{recordDisplay.boolean(healthInsurance.joined, '加入', '未加入')}</Dd>
        </Dl>
        <Dl>
          <Dt>健康保険被保険者整理番号{healthInsuranceType === 'kyokai' && '（年金整理番号）'}</Dt>
          <Dd>{recordDisplay(healthInsurance.joined && healthInsurance.health_insurance_insured_personnel_number)}</Dd>
        </Dl>
        <Dl>
          <Dt>資格取得日</Dt>
          <Dd>{recordDisplay.date(healthInsurance.joined && healthInsurance.start_on, yearStyle)}</Dd>
        </Dl>
        <Dl>
          <Dt>資格喪失日</Dt>
          <Dd>{recordDisplay.date(healthInsurance.joined && healthInsuranceEndOn, yearStyle)}</Dd>
        </Dl>
      </Grid>
    </SubPanel>
  )
}

interface WelfarePensionInsuranceProps extends InsuranceProps {
  welfarePensionInsurance: WelfarePensionInsurance
}

const WelfarePensionInsurance: FC<WelfarePensionInsuranceProps> = ({
  welfarePensionInsurance,
  retired_at,
  healthInsuranceType,
  editLink,
  yearStyle,
}) => {
  const welfarePensionInsuranceEndOn = welfarePensionInsurance.end_on || addDay(retired_at, 1)
  const welfarePensionInsuranceImage = welfarePensionInsurance.image || {}

  return (
    <SubPanel>
      <HashLink to={`${editLink}#WelfarePensionInsuranceInfomation`}>
        <SubPanel.Headding icon={<Edit size={16} />}>厚生年金保険</SubPanel.Headding>
      </HashLink>
      <Grid className={styles.panelHalf}>
        <Dl>
          <Dt>加入状況</Dt>
          <Dd>{recordDisplay.boolean(welfarePensionInsurance.joined, '加入', '未加入')}</Dd>
        </Dl>
        <Dl>
          <Dt>基礎年金番号</Dt>
          <Dd>
            {recordDisplay.numbers(
              welfarePensionInsurance.joined && welfarePensionInsurance,
              'basic_pension_number',
              2
            )}
          </Dd>
        </Dl>
        {healthInsuranceType !== 'kyokai' && (
          <Dl>
            <Dt>厚生年金保険被保険者整理番号</Dt>
            <Dd>{recordDisplay(welfarePensionInsurance.joined && welfarePensionInsurance.insured_personnel_number)}</Dd>
          </Dl>
        )}
        <Dl>
          <Dt>資格取得日</Dt>
          <Dd>{recordDisplay.date(welfarePensionInsurance.joined && welfarePensionInsurance.start_on, yearStyle)}</Dd>
        </Dl>
        <Dl>
          <Dt>資格喪失日</Dt>
          <Dd>{recordDisplay.date(welfarePensionInsurance.joined && welfarePensionInsuranceEndOn, yearStyle)}</Dd>
        </Dl>
        {welfarePensionInsurance.first_time_join && (
          <Dl>
            <Dt>初めて厚生年金保険に加入する</Dt>
            <Dd>はい</Dd>
          </Dl>
        )}
        {welfarePensionInsurance.need_detail && (
          <Dl>
            <Dt>基礎年金番号が分からない</Dt>
            <Dd>はい</Dd>
          </Dl>
        )}
        {welfarePensionInsuranceImage.filename && (
          <Dl>
            <Dt>年金手帳または基礎年金番号通知書画像</Dt>
            <Dd>{recordDisplay.file(welfarePensionInsurance.image)}</Dd>
          </Dl>
        )}
      </Grid>
    </SubPanel>
  )
}

interface HealthAndWelfarePensionInformationProps {
  employee: Employee | Record<string, never>
  yearStyle: YearStyleType
  editLink: string
  healthInsuranceType: HealthInsuranceType
}

const renderHealthAndWelfarePensionInformation: FC<HealthAndWelfarePensionInformationProps> = ({
  employee,
  yearStyle,
  editLink,
  healthInsuranceType,
}) => {
  const healthInsurance = employee.health_insurance || {}
  const welfarePensionInsurance = employee.welfare_pension_insurance || {}

  return (
    <Panel>
      <Headding>社会保険</Headding>
      <Item>
        <Grid>
          <Half>
            <HealthInsurance
              healthInsurance={healthInsurance}
              retired_at={employee?.retired_at}
              healthInsuranceType={healthInsuranceType}
              editLink={editLink}
              yearStyle={yearStyle}
            />
          </Half>
          <Half>
            <WelfarePensionInsurance
              welfarePensionInsurance={welfarePensionInsurance}
              retired_at={employee?.retired_at}
              healthInsuranceType={healthInsuranceType}
              editLink={editLink}
              yearStyle={yearStyle}
            />
          </Half>
        </Grid>
      </Item>
    </Panel>
  )
}

export const HealthAndWelfarePensionInformation = connect((state: State) => ({
  employee: state.employees.current.data,
  healthInsuranceType: get(state.employees.current.data, 'office.health_insurance_type'),
}))(renderHealthAndWelfarePensionInformation)
